const packageName = 'googleAdsClicks';

const isBool = (v) => typeof v == 'boolean';
const isRecord = (v) => !!v && typeof v == 'object' && !Array.isArray(v);
/**
 * This function validates user consent for a given provider type, not based on tagName.
 */
const hasUserConsentForProvider = (consent, provider) => {
    if (!isRecord(consent)) {
        return false;
    }
    let allowed = isBool(consent.all) ? consent.all : false;
    if (provider in consent) {
        const providerSpecific = consent[provider];
        if (isBool(providerSpecific)) {
            allowed = providerSpecific;
        }
        else if (isRecord(providerSpecific)) {
            return Object.keys(providerSpecific).some((instance) => providerSpecific[instance] === true);
        }
    }
    return allowed;
};

const upsert = (map, key, update, createDefault) => {
    const currentValue = map.has(key)
        ? map.get(key)
        : createDefault();
    return map.set(key, update(currentValue));
};

const expand = (str) => str.split(',').flatMap((entry) => {
    if (!entry.includes('-')) {
        return entry;
    }
    const result = [];
    const [start, end] = entry.split('-').map(Number);
    for (let i = start; i <= end; i++) {
        result.push(i.toString());
    }
    return result;
});
/**
 * Exported from https://en.wikipedia.org/wiki/List_of_North_American_Numbering_Plan_area_codes
 *
 * In Dev Tools, select the `tbody` element containing the area codes and run the following code,
 * replacing the emdash character with a simple endash:
 *
 * ```ts
 * [...$0.querySelectorAll('td:first-child')]
 *   .filter(cell => cell.firstChild.nodeName != 'A')
 *   .map(cell => cell.textContent.trim()).join(',')
 * ```
 */
new Set([
    ...expand('200,211,221,222,230,232,233,235,237-238,241,243,244,245,247,255,257,258-259,261,265,266,271,273,274,275,277,278,280,282,283,285-287,288,290-299'),
    ...expand('300,311,322,324,327,328,333,335,338,342,344,348-349,353,355,356,357-359,362,366,369,370-379,381,382,383-384,387,388,389,390-399'),
    ...expand('400,411,420,421-422,426-427,428,429,433,439,444,446,449,451-454,455,456,457,459,460,461-462,465,466,467,471,476,477,481-483,485-486,487,488,489,490-499'),
    ...expand('511,532,535,536,537,538,542-543,545-547,549-550,552-554,555,556,558,560,565,568,569,576,578,583,589,590-599'),
    ...expand('611,621,624,625,627,632,633,634-635,637-638,642-643,644,648,652-654,655,663,665,666,668,673-676,677,679,685,686,687,688,690-699'),
    ...expand('711,722,723,729,733,735-736,739,741,744,745-746,748,749-751,752,755,756,759,761,764,766,768,776,777,783,788,789,790-799'),
    ...expand('811,821,822,823-824,827,834,836,841-842,846,851,852-853,871,874-875,879,880-887,889,890-899'),
    ...expand('911,921,922,923,924,926,927,932,933,935,942,944,946,950,953,955,957-958,960-969,974,975,976,977,981-982,987,988,990-999'),
]);

/**
 * ISO-3166 2-leter country codes and their names
 * @see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
 */
const isoCountries = new Map([
    ['AD', 'Andorra'],
    ['AE', 'United Arab Emirates'],
    ['AF', 'Afghanistan'],
    ['AG', 'Antigua and Barbuda'],
    ['AI', 'Anguilla'],
    ['AL', 'Albania'],
    ['AM', 'Armenia'],
    ['AO', 'Angola'],
    ['AQ', 'Antarctica'],
    ['AR', 'Argentina'],
    ['AS', 'American Samoa'],
    ['AT', 'Austria'],
    ['AU', 'Australia'],
    ['AW', 'Aruba'],
    ['AX', 'Åland Islands'],
    ['AZ', 'Azerbaijan'],
    ['BA', 'Bosnia and Herzegovina'],
    ['BB', 'Barbados'],
    ['BD', 'Bangladesh'],
    ['BE', 'Belgium'],
    ['BF', 'Burkina Faso'],
    ['BG', 'Bulgaria'],
    ['BH', 'Bahrain'],
    ['BI', 'Burundi'],
    ['BJ', 'Benin'],
    ['BL', 'Saint Barthélemy'],
    ['BM', 'Bermuda'],
    ['BN', 'Brunei Darussalam'],
    ['BO', 'Bolivia, Plurinational State of'],
    ['BQ', 'Bonaire, Sint Eustatius and Saba'],
    ['BR', 'Brazil'],
    ['BS', 'Bahamas'],
    ['BT', 'Bhutan'],
    ['BV', 'Bouvet Island'],
    ['BW', 'Botswana'],
    ['BY', 'Belarus'],
    ['BZ', 'Belize'],
    ['CA', 'Canada'],
    ['CC', 'Cocos (Keeling) Islands'],
    ['CD', 'Congo, Democratic Republic of the'],
    ['CF', 'Central African Republic'],
    ['CG', 'Congo'],
    ['CH', 'Switzerland'],
    ['CI', "Côte d'Ivoire"],
    ['CK', 'Cook Islands'],
    ['CL', 'Chile'],
    ['CM', 'Cameroon'],
    ['CN', 'China'],
    ['CO', 'Colombia'],
    ['CR', 'Costa Rica'],
    ['CU', 'Cuba'],
    ['CV', 'Cabo Verde'],
    ['CW', 'Curaçao'],
    ['CX', 'Christmas Island'],
    ['CY', 'Cyprus'],
    ['CZ', 'Czechia'],
    ['DE', 'Germany'],
    ['DJ', 'Djibouti'],
    ['DK', 'Denmark'],
    ['DM', 'Dominica'],
    ['DO', 'Dominican Republic'],
    ['DZ', 'Algeria'],
    ['EC', 'Ecuador'],
    ['EE', 'Estonia'],
    ['EG', 'Egypt'],
    ['EH', 'Western Sahara'],
    ['ER', 'Eritrea'],
    ['ES', 'Spain'],
    ['ET', 'Ethiopia'],
    ['EU', 'European Union'],
    ['FI', 'Finland'],
    ['FJ', 'Fiji'],
    ['FK', 'Falkland Islands (Malvinas)'],
    ['FM', 'Micronesia, Federated States of'],
    ['FO', 'Faroe Islands'],
    ['FR', 'France'],
    ['GA', 'Gabon'],
    ['GB', 'United Kingdom of Great Britain and Northern Ireland'],
    ['GD', 'Grenada'],
    ['GE', 'Georgia'],
    ['GF', 'French Guiana'],
    ['GG', 'Guernsey'],
    ['GH', 'Ghana'],
    ['GI', 'Gibraltar'],
    ['GL', 'Greenland'],
    ['GM', 'Gambia'],
    ['GN', 'Guinea'],
    ['GP', 'Guadeloupe'],
    ['GQ', 'Equatorial Guinea'],
    ['GR', 'Greece'],
    ['GS', 'South Georgia and the South Sandwich Islands'],
    ['GT', 'Guatemala'],
    ['GU', 'Guam'],
    ['GW', 'Guinea-Bissau'],
    ['GY', 'Guyana'],
    ['HK', 'Hong Kong'],
    ['HM', 'Heard Island and McDonald Islands'],
    ['HN', 'Honduras'],
    ['HR', 'Croatia'],
    ['HT', 'Haiti'],
    ['HU', 'Hungary'],
    ['ID', 'Indonesia'],
    ['IE', 'Ireland'],
    ['IL', 'Israel'],
    ['IM', 'Isle of Man'],
    ['IN', 'India'],
    ['IO', 'British Indian Ocean Territory'],
    ['IQ', 'Iraq'],
    ['IR', 'Iran, Islamic Republic of'],
    ['IS', 'Iceland'],
    ['IT', 'Italy'],
    ['JE', 'Jersey'],
    ['JM', 'Jamaica'],
    ['JO', 'Jordan'],
    ['JP', 'Japan'],
    ['KE', 'Kenya'],
    ['KG', 'Kyrgyzstan'],
    ['KH', 'Cambodia'],
    ['KI', 'Kiribati'],
    ['KM', 'Comoros'],
    ['KN', 'Saint Kitts and Nevis'],
    ['KP', "Korea, Democratic People's Republic of"],
    ['KR', 'Korea, Republic of'],
    ['KW', 'Kuwait'],
    ['KY', 'Cayman Islands'],
    ['KZ', 'Kazakhstan'],
    ['LA', "Lao People's Democratic Republic"],
    ['LB', 'Lebanon'],
    ['LC', 'Saint Lucia'],
    ['LI', 'Liechtenstein'],
    ['LK', 'Sri Lanka'],
    ['LR', 'Liberia'],
    ['LS', 'Lesotho'],
    ['LT', 'Lithuania'],
    ['LU', 'Luxembourg'],
    ['LV', 'Latvia'],
    ['LY', 'Libya'],
    ['MA', 'Morocco'],
    ['MC', 'Monaco'],
    ['MD', 'Moldova, Republic of'],
    ['ME', 'Montenegro'],
    ['MF', 'Saint Martin (French part)'],
    ['MG', 'Madagascar'],
    ['MH', 'Marshall Islands'],
    ['MK', 'North Macedonia'],
    ['ML', 'Mali'],
    ['MM', 'Myanmar'],
    ['MN', 'Mongolia'],
    ['MO', 'Macao'],
    ['MP', 'Northern Mariana Islands'],
    ['MQ', 'Martinique'],
    ['MR', 'Mauritania'],
    ['MS', 'Montserrat'],
    ['MT', 'Malta'],
    ['MU', 'Mauritius'],
    ['MV', 'Maldives'],
    ['MW', 'Malawi'],
    ['MX', 'Mexico'],
    ['MY', 'Malaysia'],
    ['MZ', 'Mozambique'],
    ['NA', 'Namibia'],
    ['NC', 'New Caledonia'],
    ['NE', 'Niger'],
    ['NF', 'Norfolk Island'],
    ['NG', 'Nigeria'],
    ['NI', 'Nicaragua'],
    ['NL', 'Netherlands, Kingdom of the'],
    ['NO', 'Norway'],
    ['NP', 'Nepal'],
    ['NR', 'Nauru'],
    ['NU', 'Niue'],
    ['NZ', 'New Zealand'],
    ['OM', 'Oman'],
    ['PA', 'Panama'],
    ['PE', 'Peru'],
    ['PF', 'French Polynesia'],
    ['PG', 'Papua New Guinea'],
    ['PH', 'Philippines'],
    ['PK', 'Pakistan'],
    ['PL', 'Poland'],
    ['PM', 'Saint Pierre and Miquelon'],
    ['PN', 'Pitcairn'],
    ['PR', 'Puerto Rico'],
    ['PS', 'Palestine, State of'],
    ['PT', 'Portugal'],
    ['PW', 'Palau'],
    ['PY', 'Paraguay'],
    ['QA', 'Qatar'],
    ['RE', 'Réunion'],
    ['RO', 'Romania'],
    ['RS', 'Serbia'],
    ['RU', 'Russian Federation'],
    ['RW', 'Rwanda'],
    ['SA', 'Saudi Arabia'],
    ['SB', 'Solomon Islands'],
    ['SC', 'Seychelles'],
    ['SD', 'Sudan'],
    ['SE', 'Sweden'],
    ['SG', 'Singapore'],
    ['SH', 'Saint Helena, Ascension and Tristan da Cunha'],
    ['SI', 'Slovenia'],
    ['SJ', 'Svalbard and Jan Mayen'],
    ['SK', 'Slovakia'],
    ['SL', 'Sierra Leone'],
    ['SM', 'San Marino'],
    ['SN', 'Senegal'],
    ['SO', 'Somalia'],
    ['SR', 'Suriname'],
    ['SS', 'South Sudan'],
    ['ST', 'Sao Tome and Principe'],
    ['SV', 'El Salvador'],
    ['SX', 'Sint Maarten (Dutch part)'],
    ['SY', 'Syrian Arab Republic'],
    ['SZ', 'Eswatini'],
    ['TC', 'Turks and Caicos Islands'],
    ['TD', 'Chad'],
    ['TF', 'French Southern Territories'],
    ['TG', 'Togo'],
    ['TH', 'Thailand'],
    ['TJ', 'Tajikistan'],
    ['TK', 'Tokelau'],
    ['TL', 'Timor-Leste'],
    ['TM', 'Turkmenistan'],
    ['TN', 'Tunisia'],
    ['TO', 'Tonga'],
    ['TR', 'Türkiye'],
    ['TT', 'Trinidad and Tobago'],
    ['TV', 'Tuvalu'],
    ['TW', 'Taiwan, Province of China'],
    ['TZ', 'Tanzania, United Republic of'],
    ['UA', 'Ukraine'],
    ['UG', 'Uganda'],
    ['UK', 'United Kingdom'],
    ['UM', 'United States Minor Outlying Islands'],
    ['US', 'United States of America'],
    ['UY', 'Uruguay'],
    ['UZ', 'Uzbekistan'],
    ['VA', 'Holy See'],
    ['VC', 'Saint Vincent and the Grenadines'],
    ['VE', 'Venezuela, Bolivarian Republic of'],
    ['VG', 'Virgin Islands (British)'],
    ['VI', 'Virgin Islands (U.S.)'],
    ['VN', 'Viet Nam'],
    ['VU', 'Vanuatu'],
    ['WF', 'Wallis and Futuna'],
    ['WS', 'Samoa'],
    ['YE', 'Yemen'],
    ['YT', 'Mayotte'],
    ['ZA', 'South Africa'],
    ['ZM', 'Zambia'],
    ['ZW', 'Zimbabwe'],
]);
/**
 * ISO-3166 US state ISO codes
 * @see https://en.wikipedia.org/wiki/ISO_3166-2:US
 * */
const usStates = new Map([
    ['US-AL', 'Alabama'],
    ['US-AK', 'Alaska'],
    ['US-AZ', 'Arizona'],
    ['US-AR', 'Arkansas'],
    ['US-CA', 'California'],
    ['US-CO', 'Colorado'],
    ['US-CT', 'Connecticut'],
    ['US-DE', 'Delaware'],
    ['US-FL', 'Florida'],
    ['US-GA', 'Georgia'],
    ['US-HI', 'Hawaii'],
    ['US-ID', 'Idaho'],
    ['US-IL', 'Illinois'],
    ['US-IN', 'Indiana'],
    ['US-IA', 'Iowa'],
    ['US-KS', 'Kansas'],
    ['US-KY', 'Kentucky'],
    ['US-LA', 'Louisiana'],
    ['US-ME', 'Maine'],
    ['US-MD', 'Maryland'],
    ['US-MA', 'Massachusetts'],
    ['US-MI', 'Michigan'],
    ['US-MN', 'Minnesota'],
    ['US-MS', 'Mississippi'],
    ['US-MO', 'Missouri'],
    ['US-MT', 'Montana'],
    ['US-NE', 'Nebraska'],
    ['US-NV', 'Nevada'],
    ['US-NH', 'New Hampshire'],
    ['US-NJ', 'New Jersey'],
    ['US-NM', 'New Mexico'],
    ['US-NY', 'New York'],
    ['US-NC', 'North Carolina'],
    ['US-ND', 'North Dakota'],
    ['US-OH', 'Ohio'],
    ['US-OK', 'Oklahoma'],
    ['US-OR', 'Oregon'],
    ['US-PA', 'Pennsylvania'],
    ['US-RI', 'Rhode Island'],
    ['US-SC', 'South Carolina'],
    ['US-SD', 'South Dakota'],
    ['US-TN', 'Tennessee'],
    ['US-TX', 'Texas'],
    ['US-UT', 'Utah'],
    ['US-VT', 'Vermont'],
    ['US-VA', 'Virginia'],
    ['US-WA', 'Washington'],
    ['US-WV', 'West Virginia'],
    ['US-WI', 'Wisconsin'],
    ['US-WY', 'Wyoming'],
    ['US-DC', 'District of Columbia'],
    ['US-AS', 'American Samoa'],
    ['US-GU', 'Guam'],
    ['US-MP', 'Northern Mariana Islands'],
    ['US-PR', 'Puerto Rico'],
    ['US-UM', 'United States Minor Outlying Islands'],
    ['US-VI', 'Virgin Islands, U.S.'],
]);
new Set([...isoCountries.keys(), ...usStates.keys()]);

// eslint-disable-next-line @nx/enforce-module-boundaries
// TODO: once categories becomes a stable field, we need to remove this and start using categories data for google consent mode.
const getGoogleConsentFromChannels = (consent) => {
    const gadsConsent = hasUserConsentForProvider(consent, 'googleAdsClicks');
    const ga4Consent = hasUserConsentForProvider(consent, 'googleAnalytics4');
    return {
        analytics_storage: ga4Consent ? 'granted' : 'denied',
        ad_storage: gadsConsent ? 'granted' : 'denied',
        ad_user_data: gadsConsent ? 'granted' : 'denied',
        ad_personalization: gadsConsent ? 'granted' : 'denied',
    };
};

// eslint-disable-next-line @nx/enforce-module-boundaries
const initGAds = (ID, consent, executionContext) => {
    var _a;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    let isConsentInitialised = false;
    for (const otherProvider of ['googleAdsClicks', 'googleAnalytics4']) {
        if ((_a = executionContext.get(otherProvider)) === null || _a === void 0 ? void 0 : _a.get('gtag')) {
            isConsentInitialised = true;
            break;
        }
    }
    const data = getGoogleConsentFromChannels(consent);
    if (isConsentInitialised) {
        window.gtag('consent', 'update', data);
    }
    else {
        window.gtag('consent', 'default', {
            ...data,
            functional_storage: 'granted',
            personalization_storage: 'granted',
            security_storage: 'granted',
        });
    }
    upsert(executionContext, packageName, (providerMap) => providerMap.set('gtag', true), () => new Map());
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.gtag('js', new Date());
    const element = document.createElement('script');
    element.async = !0;
    element.src = `https://www.googletagmanager.com/gtag/js?id=${ID}`;
    const script = document.getElementsByTagName('script')[0];
    if (script && script.parentNode) {
        script.parentNode.insertBefore(element, script);
    }
};
const init = ({ manifest, consentData, executionContext }) => {
    var _a, _b;
    if (!window ||
        !((_a = manifest === null || manifest === void 0 ? void 0 : manifest.variables) === null || _a === void 0 ? void 0 : _a['tagId']) ||
        manifest.variables['flowType'] === 'server') {
        return;
    }
    const customConfig = {
        send_page_view: false,
    };
    if (((_b = manifest.variables) === null || _b === void 0 ? void 0 : _b['enhancedConversionsSetting']) === 'gtag') {
        customConfig['allow_enhanced_conversions'] = true;
    }
    if (!window.google_tag_manager ||
        !window.google_tag_manager[manifest.variables['tagId']]) {
        initGAds(manifest.variables['tagId'], consentData.consent, executionContext);
    }
    if (window.gtag) {
        window.gtag('config', manifest.variables['tagId'], customConfig);
    }
};

const getIdentifierValue = (identifier, contentItem) => {
    let identifierValue = identifier.trim();
    if (!contentItem) {
        return identifierValue;
    }
    identifierValue = identifierValue.replaceAll('{productId}', contentItem.id);
    identifierValue = identifierValue.replaceAll('{variantId}', contentItem.variantId || '');
    identifierValue = identifierValue.replaceAll('{sku}', contentItem.sku || '');
    return identifierValue;
};
const getConversionLabel = (eventName, manifestVariables) => {
    if (!manifestVariables) {
        return '';
    }
    let mappings = [];
    try {
        mappings = manifestVariables['conversionMappings'];
    }
    catch (e) {
        mappings = [];
        console.log(e);
    }
    const mapping = mappings.find((mappingItem) => mappingItem.eventName === eventName);
    return mapping === null || mapping === void 0 ? void 0 : mapping.browserConversionLabel;
};
const getEventData = (data) => {
    const eventData = {};
    if (!data) {
        return eventData;
    }
    if (data['value']) {
        eventData.value = data['value'];
    }
    if (data['currency']) {
        eventData.currency = data['currency'];
    }
    if (data['orderId']) {
        eventData.transaction_id = data['orderId'];
    }
    return eventData;
};
const getCartData = (variables, data) => {
    const cartData = {};
    const merchantId = parseInt(variables['merchantId']);
    if (!isNaN(merchantId)) {
        cartData.aw_merchant_id = merchantId;
    }
    if (variables['identifierFormat'] && Array.isArray(data === null || data === void 0 ? void 0 : data['contents'])) {
        cartData.items = data['contents'].map((content) => {
            return {
                id: getIdentifierValue(variables['identifierFormat'], content),
                price: content.item_price,
                quantity: content.quantity,
            };
        });
    }
    return cartData;
};
const getUserData = (destination) => {
    var _a, _b, _c;
    let kv = {};
    const userData = {};
    const address = {};
    try {
        kv =
            (_c = (_b = (_a = JSON.parse(window.sessionStorage.getItem('_workerStoreMultiple') || '{}')) === null || _a === void 0 ? void 0 : _a[destination]) === null || _b === void 0 ? void 0 : _b['kv']) !== null && _c !== void 0 ? _c : {};
    }
    catch (e) {
        console.error('Unable to get user data from session storage');
        kv = {};
    }
    if (kv['email']) {
        userData['email'] = kv['email'];
    }
    if (kv['phone']) {
        userData['phone_number'] = kv['phone'];
    }
    if (kv['firstName']) {
        address['first_name'] = kv['firstName'];
    }
    if (kv['lastName']) {
        address['last_name'] = kv['lastName'];
    }
    if (kv['zip']) {
        address['postal_code'] = kv['zip'];
    }
    if (kv['city']) {
        address['city'] = kv['city'];
    }
    if (Object.keys(address).length) {
        userData['address'] = [address];
    }
    return userData;
};
const handleTag = ({ data, eventName, manifestVariables, destination, }) => {
    if (!eventName ||
        !window.gtag ||
        !(manifestVariables === null || manifestVariables === void 0 ? void 0 : manifestVariables['tagId']) ||
        manifestVariables['flowType'] === 'server') {
        return;
    }
    const conversionLabel = getConversionLabel(eventName, manifestVariables);
    if (!conversionLabel) {
        return;
    }
    if (manifestVariables['enhancedConversionsSetting'] === 'gtag' &&
        destination) {
        window.gtag('set', 'user_data', getUserData(destination));
    }
    const eventData = getEventData(data);
    const cartData = getCartData(manifestVariables, data);
    window.gtag('event', 'conversion', {
        send_to: conversionLabel,
        ...eventData,
        ...cartData,
        app: 'blotout',
    });
};

const getCookieValue = (key) => {
    var _a;
    try {
        if (!document || !document.cookie) {
            return '';
        }
        const cookies = parseCookies(document.cookie);
        return (_a = cookies[key]) !== null && _a !== void 0 ? _a : '';
    }
    catch {
        return '';
    }
};
const parseCookies = (cookie) => {
    return Object.fromEntries(cookie
        .split(/;\s+/)
        .map((r) => r.split('=').map((str) => str.trim()))
        .map(([cookieKey, ...cookieValues]) => {
        const cookieValue = cookieValues.join('=');
        if (!cookieKey) {
            return [];
        }
        let decodedValue = '';
        if (cookieValue) {
            try {
                decodedValue = decodeURIComponent(cookieValue);
            }
            catch (e) {
                console.log(`Unable to decode cookie ${cookieKey}: ${e}`);
                decodedValue = cookieValue;
            }
        }
        return [cookieKey, decodedValue];
    }));
};

const tag = ({ data, eventName, manifestVariables, eventId, destination, }) => {
    let isLoaded;
    if (manifestVariables['flowType'] === 'browser') {
        isLoaded = true;
    }
    else if (manifestVariables['flowType'] === 'server') {
        isLoaded = false;
    }
    else {
        isLoaded = window && window.gtag && !!getCookieValue('_gcl_au');
    }
    if (isLoaded) {
        handleTag({
            data,
            eventName,
            manifestVariables,
            eventId,
            destination,
        });
    }
    return {
        loaded: isLoaded,
        sdkVersion: "1.15.0" ,
    };
};

const consent = ({ consentData }) => {
    if (!(window === null || window === void 0 ? void 0 : window.gtag)) {
        return;
    }
    const data = getGoogleConsentFromChannels(consentData.consent);
    window.gtag('consent', 'update', {
        ...data,
        personalization_storage: 'granted',
        security_storage: 'granted',
        functional_storage: 'granted',
    });
};

// eslint-disable-next-line @nx/enforce-module-boundaries
const data = {
    name: packageName,
    init,
    tag,
    consent,
};
try {
    if (window) {
        if (!window.edgetagProviders) {
            window.edgetagProviders = [];
        }
        window.edgetagProviders.push(data);
    }
}
catch {
    // No window
}

export { data as default };
